import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { apiClient } from "../../../config/api";
import API_CONFIG from "../../../config/api";

const COLORS = {
  New: "#38bdf8",
  Active: "#f97316",
  Resolved: "#22c55e",
  default: "#6b7280",
};

const statusKeys = ["New", "Active", "Resolved"];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="risky-endpoints__tooltip">
      <div className="risky-endpoints__tooltip-title">{label}</div>
      {payload.map((p) => (
        <div key={p.name} className="risky-endpoints__tooltip-row" style={{ color: p.color }}>
          {p.name}: {p.value}
        </div>
      ))}
      <div className="risky-endpoints__tooltip-total">
        Total: {payload.reduce((sum, p) => sum + p.value, 0)}
      </div>
    </div>
  );
};

export default function TopRiskyEndpoints({ range = "24h", clientId = null }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const params = {};
        if (range) params.range = range;
        if (range.endsWith("d")) {
          const days = parseInt(range.replace("d", ""), 10);
          if (!Number.isNaN(days)) params.days = days;
        }
        const { data } = await apiClient.get(API_CONFIG.ENDPOINTS.RISKY_ENDPOINTS, { params });
        const rows = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];

        if (!mounted) return;

        const mapped = rows.slice(0, 10).map((r) => {
          const byStatus = r.byStatus || {};
          const total = Number(r.total ?? 0) || 0;
          const entry = { name: r.host || r.Host || "Unknown Host", total };
          statusKeys.forEach((k) => {
            entry[k] = Number(byStatus[k] ?? 0) || 0;
          });
          return entry;
        });

        setData(mapped);
      } catch (err) {
        if (mounted) {
          setError(err.response?.data?.message || err.message || "Failed to load risky endpoints");
          setData([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchData();
    return () => {
      mounted = false;
    };
  }, [range, clientId]);

  const stackedKeys = useMemo(() => statusKeys, []);

  return (
    <Box className="risky-endpoints">
      <Box sx={{ marginBottom: "16px" }}>
        <Typography variant="h5" className="dashboard__widget-title">
          Top 10 Risky Endpoints
        </Typography>
        <Typography variant="body2" className="dashboard__widget-subtitle">
          Devices with highest security risk exposure
        </Typography>
      </Box>
      <Box className="risky-endpoints__chart">
        {loading && <div className="text-muted">Loading endpoints…</div>}
        {error && <div className="text-muted">Error: {error}</div>}
        {!loading && !error && data.length === 0 && (
          <div className="text-muted">No endpoints found for selected filters.</div>
        )}
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={data} margin={{ top: 20, right: 20, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#CBD5E0" opacity={0.2} />
            <XAxis dataKey="name" stroke="#767676" style={{ fontSize: "12px" }} />
            <YAxis
              stroke="#767676"
              style={{ fontSize: "12px" }}
              tick={{ fill: "#767676" }}
              tickFormatter={(value) => `${value}`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {stackedKeys.map((key) => (
              <Bar key={key} dataKey={key} stackId="a" fill={COLORS[key] || COLORS.default} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

